/* Tradeline facets: in-place filtering, sorting, pagination, "load more" and grid/list view
 * for collection and search pages. Without JS the facets form submits as a normal GET.
 *
 * Markup contract (see snippets/facets.liquid, snippets/pagination.liquid):
 *   <tl-facets data-section-id>                      wrapper
 *   [data-facets-form]                               GET form (sort select joins via form="")
 *   [data-facets-region][id]                         replaced from the Section Rendering response
 *   [data-facet-link]                                links handled in place (pills, clear, pages)
 *   [data-load-more]                                 appends the next page to [data-product-grid]
 *   [data-load-more][data-infinite]                  same, triggered when it scrolls into view
 *                                                    (a few pages, then the button stays for clicks
 *                                                    so the footer can still be reached)
 *   [data-append][id]                                other lists that grow with "load more"
 *   [data-view-btn="grid|list"]                      view toggle, remembered per visitor
 */
(() => {
  if (customElements.get('tl-facets')) return;

  const cache = new Map();
  const T = () => window.Tradeline || {};
  const debounce = (fn, ms) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };
  const VIEW_KEY = 'collection-view';

  class TlFacets extends HTMLElement {
    connectedCallback() {
      this.sid = this.dataset.sectionId;
      this.debouncedSubmit = debounce(() => this.submit(), 500);
      this.quickSubmit = debounce(() => this.submit(), 200);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('toggle', this.onToggle, true);

      this.onPop = (e) => { if (e.state && e.state.tlFacets === this.sid) this.render(location.search.slice(1), false); };
      this.onDocClick = (e) => { if (this.isHorizontal() && !e.target.closest('.tl-facet')) this.closeDropdowns(); };
      this.onKey = (e) => { if (e.key === 'Escape' && this.isHorizontal()) this.closeDropdowns(true); };
      window.addEventListener('popstate', this.onPop);
      document.addEventListener('click', this.onDocClick);
      document.addEventListener('keydown', this.onKey);
      try { history.replaceState(Object.assign({}, history.state, { tlFacets: this.sid }), ''); } catch (e) { /* ignore */ }

      this.applyView(this.storedView());
      this.watchDrawer();
      this.autoLoads = 0;
      this.watchInfinite();
    }

    watchInfinite() {
      if (this.infiniteObserver) this.infiniteObserver.disconnect();
      const btn = this.querySelector('[data-load-more][data-infinite]');
      if (!btn || !('IntersectionObserver' in window) || this.autoLoads >= 4) return;
      this.infiniteObserver = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting) || btn.classList.contains('is-loading')) return;
        this.infiniteObserver.disconnect();
        this.autoLoads += 1;
        this.loadMore(btn, true);
      }, { rootMargin: '0px 0px 400px 0px' });
      this.infiniteObserver.observe(btn);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPop);
      document.removeEventListener('click', this.onDocClick);
      document.removeEventListener('keydown', this.onKey);
      if (this.observer) this.observer.disconnect();
      if (this.infiniteObserver) this.infiniteObserver.disconnect();
    }

    get form() { return this.querySelector('[data-facets-form]'); }
    get drawer() { return document.getElementById('FacetsDrawer-' + this.sid); }

    isHorizontal() { return this.classList.contains('tl-facets--horizontal') && window.matchMedia('(min-width: 990px)').matches; }

    /* ---------- Events ---------- */
    onChange(e) {
      const t = e.target;
      if (t.matches('[data-facet-price]')) return this.debouncedSubmit();
      if (t.matches('[data-facets-sort]') || (this.form && this.form.contains(t))) this.quickSubmit();
    }

    onInput(e) { if (e.target.matches('[data-facet-price]')) this.debouncedSubmit(); }

    onSubmit(e) {
      if (!e.target.matches('[data-facets-form]')) return;
      e.preventDefault();
      this.submit();
      const d = this.drawer;
      if (d && d.classList.contains('is-open') && T().closeDrawer) T().closeDrawer(d);
    }

    onClick(e) {
      const link = e.target.closest('a[data-facet-link]');
      if (link) {
        e.preventDefault();
        const url = new URL(link.href, location.origin);
        const isPage = !!link.closest('.tl-pagination');
        this.render(url.searchParams.toString()).then(() => { if (isPage) this.scrollTop(); });
        return;
      }
      const more = e.target.closest('[data-load-more]');
      if (more) { e.preventDefault(); this.loadMore(more); return; }
      const view = e.target.closest('[data-view-btn]');
      if (view) { this.applyView(view.dataset.viewBtn); T().store && T().store.set(VIEW_KEY, view.dataset.viewBtn); return; }
      const showMore = e.target.closest('[data-facet-more]');
      if (showMore) this.toggleMore(showMore);
    }

    onToggle(e) {
      const d = e.target;
      if (!d.matches || !d.matches('details.tl-facet') || !d.open || !this.isHorizontal()) return;
      this.querySelectorAll('details.tl-facet[open]').forEach((o) => { if (o !== d) o.open = false; });
    }

    closeDropdowns(refocus) {
      this.querySelectorAll('details.tl-facet[open]').forEach((d) => {
        d.open = false;
        if (refocus && d.contains(document.activeElement)) d.querySelector('summary').focus();
      });
    }

    toggleMore(btn) {
      const list = document.getElementById(btn.getAttribute('aria-controls'));
      if (!list) return;
      const expanded = list.classList.toggle('is-expanded');
      btn.setAttribute('aria-expanded', String(expanded));
      if (!btn.dataset.labelMore) btn.dataset.labelMore = btn.textContent.trim();
      btn.textContent = expanded ? btn.dataset.labelLess : btn.dataset.labelMore;
    }

    /* ---------- Rendering ---------- */
    params() {
      const p = new URLSearchParams();
      if (!this.form) return '';
      for (const [k, v] of new FormData(this.form).entries()) {
        if (typeof v === 'string' && v.trim() !== '') p.append(k, v.trim());
      }
      return p.toString();
    }

    submit() { return this.render(this.params()); }

    fetchSection(search) {
      if (cache.has(search)) return cache.get(search);
      const p = new URLSearchParams(search);
      p.set('section_id', this.sid);
      const req = fetch(location.pathname + '?' + p.toString()).then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      });
      cache.set(search, req);
      req.catch(() => cache.delete(search));
      return req;
    }

    async render(search, push = true) {
      const url = location.pathname + (search ? '?' + search : '');
      if (push) history.pushState({ tlFacets: this.sid }, '', url);
      this.setBusy(true);
      try {
        this.swap(await this.fetchSection(search));
      } catch (err) {
        window.location.href = url;
      } finally {
        this.setBusy(false);
      }
    }

    swap(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const openState = {};
      this.querySelectorAll('details.tl-facet').forEach((d) => { openState[d.dataset.facet] = d.open; });
      const expandedLists = Array.from(this.querySelectorAll('.tl-facet__list.is-expanded')).map((l) => l.id);
      const active = document.activeElement;
      const activeId = active && this.contains(active) ? active.id : '';
      const typedValue = active && active.matches && active.matches('[data-facet-price]') ? active.value : null;

      this.querySelectorAll('[data-facets-region]').forEach((el) => {
        const fresh = doc.getElementById(el.id);
        if (!fresh) return;
        if (el.getAttribute('role') === 'status') { el.textContent = fresh.textContent; return; }
        el.replaceWith(fresh);
      });

      this.querySelectorAll('details.tl-facet').forEach((d) => {
        if (d.dataset.facet in openState) d.open = openState[d.dataset.facet];
      });
      expandedLists.forEach((id) => {
        const list = document.getElementById(id);
        const btn = this.querySelector(`[data-facet-more][aria-controls="${id}"]`);
        if (list && btn) this.toggleMore(btn);
      });
      if (activeId) {
        const el = document.getElementById(activeId);
        if (el) {
          if (typedValue !== null) el.value = typedValue;
          el.focus({ preventScroll: true });
        }
      }
      this.applyView(this.storedView());
      this.syncToggles(this);
      this.autoLoads = 0;
      this.watchInfinite();
    }

    async loadMore(btn, auto = false) {
      btn.classList.add('is-loading');
      btn.setAttribute('aria-disabled', 'true');
      try {
        const url = new URL(btn.href, location.origin);
        const html = await this.fetchSection(url.searchParams.toString());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        let firstNew = null;
        this.querySelectorAll('[data-product-grid], [data-append]').forEach((list) => {
          const fresh = doc.getElementById(list.id);
          if (!fresh) return;
          const items = Array.from(fresh.children);
          if (!firstNew && items[0]) firstNew = items[0];
          list.append(...items);
        });
        const wrap = this.querySelector('#Pagination-' + this.sid);
        const freshWrap = doc.getElementById('Pagination-' + this.sid);
        if (wrap) {
          const status = wrap.querySelector('[role="status"]');
          const freshStatus = freshWrap && freshWrap.querySelector('[role="status"]');
          if (status && freshStatus) status.textContent = freshStatus.textContent;
          const bar = wrap.querySelector('.tl-load-more__bar');
          const freshBar = freshWrap && freshWrap.querySelector('.tl-load-more__bar');
          if (bar && freshBar) bar.setAttribute('style', freshBar.getAttribute('style'));
          const freshBtn = freshWrap && freshWrap.querySelector('[data-load-more]');
          if (freshBtn) btn.replaceWith(freshBtn); else btn.remove();
        }
        this.syncToggles(this);
        this.watchInfinite();
        if (firstNew && !auto) {
          const focusable = firstNew.querySelector('a[href]:not([tabindex="-1"]), button');
          if (focusable) focusable.focus({ preventScroll: true });
        }
      } catch (err) {
        window.location.href = btn.href;
      } finally {
        btn.classList.remove('is-loading');
        btn.removeAttribute('aria-disabled');
      }
    }

    setBusy(on) {
      this.classList.toggle('is-loading', on);
      const results = this.querySelector('.tl-facets__results');
      if (results) results.setAttribute('aria-busy', String(on));
    }

    scrollTop() {
      const top = this.getBoundingClientRect().top + window.scrollY - 96;
      if (top < window.scrollY) window.scrollTo({ top, behavior: 'smooth' });
    }

    /* ---------- View toggle ---------- */
    storedView() {
      if (!this.querySelector('[data-view-btn]')) return this.dataset.defaultView || 'grid';
      const s = T().store ? T().store.get(VIEW_KEY, null) : null;
      return s === 'list' || s === 'grid' ? s : this.dataset.defaultView || 'grid';
    }

    applyView(view) {
      this.querySelectorAll('[data-product-grid]').forEach((g) => { g.dataset.view = view; });
      this.querySelectorAll('[data-view-btn]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.viewBtn === view)));
    }

    /* ---------- Misc ---------- */
    syncToggles(root) {
      const t = T();
      root.querySelectorAll('[data-wishlist]').forEach((b) => b.setAttribute('aria-pressed', String(!!(t.wishlist && t.wishlist.has(b.dataset.wishlist)))));
      root.querySelectorAll('[data-compare]').forEach((b) => b.setAttribute('aria-pressed', String(!!(t.compare && t.compare.has(b.dataset.compare)))));
    }

    watchDrawer() {
      const d = this.drawer;
      if (!d || !window.MutationObserver) return;
      const sync = () => {
        const open = d.classList.contains('is-open');
        this.querySelectorAll(`[aria-controls="${d.id}"]`).forEach((b) => b.setAttribute('aria-expanded', String(open)));
      };
      this.observer = new MutationObserver(sync);
      this.observer.observe(d, { attributes: true, attributeFilter: ['class'] });
    }
  }
  customElements.define('tl-facets', TlFacets);

  /* ---------- Expandable text (collection descriptions) ---------- */
  const initClamp = (root) => {
    root.querySelectorAll('[data-clamp-toggle]').forEach((btn) => {
      const box = document.getElementById(btn.getAttribute('aria-controls'));
      if (!box || btn.dataset.ready) return;
      btn.dataset.ready = '1';
      if (box.scrollHeight <= box.clientHeight + 4) { box.classList.add('is-expanded'); return; }
      btn.hidden = false;
      btn.addEventListener('click', () => {
        const open = box.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', String(open));
        btn.textContent = open ? btn.dataset.labelLess : btn.dataset.labelMore;
      });
    });
  };
  const ready = () => initClamp(document);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', ready); else ready();
  document.addEventListener('shopify:section:load', (e) => initClamp(e.target));
})();
